import React, { useState, useEffect, useCallback } from 'react';
import { Modal } from '@byscott-io/corebyscott';
import { useParams, Link } from 'react-router-dom';
import { affiliates, apps, referralCodes } from '../api/client';
import { AffiliateModal } from './AffiliatesPage';

export default function AffiliateDetailPage() {
  const { affiliateId } = useParams();
  const [affiliate, setAffiliate] = useState(null);
  const [appsById, setAppsById] = useState({});
  const [appList, setAppList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingAffiliate, setEditingAffiliate] = useState(false);
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [editingCode, setEditingCode] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [affiliateRes, appsRes] = await Promise.all([
        affiliates.get(affiliateId),
        apps.list(),
      ]);
      setAffiliate(affiliateRes.data);
      const list = appsRes.data.apps || [];
      setAppList(list);
      setAppsById(Object.fromEntries(list.map((a) => [a.id, a])));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load affiliate');
    } finally {
      setLoading(false);
    }
  }, [affiliateId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDeactivateCode = async (code) => {
    if (!confirm(`Deactivate code "${code.code}"? It will stop resolving.`)) return;
    try {
      await referralCodes.delete(code.app_id, code.id);
      loadData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to deactivate code');
    }
  };

  if (loading) return <div className="p-6 text-gray-500">Loading...</div>;
  if (error) return <div className="p-6"><div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">{error}</div></div>;

  // Group the affiliate's codes by the app they market.
  const byApp = {};
  (affiliate.referral_codes || []).forEach((code) => {
    (byApp[code.app_id] ||= []).push(code);
  });
  const markedAppIds = Object.keys(byApp);

  const commissionLabel = (code) => {
    const parts = [];
    if (code.commission_percent > 0) parts.push(`${code.commission_percent}%`);
    if (code.commission_cents > 0) parts.push(`$${(code.commission_cents / 100).toFixed(2)} flat`);
    return parts.join(' + ') || '—';
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <Link to="/app/affiliates" className="text-blue-600 hover:text-blue-800">← Back to Affiliates</Link>
      </div>

      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-2xl font-bold">
            {affiliate.name}
            {!affiliate.active && <span className="ml-3 px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600 align-middle">Inactive</span>}
          </h1>
          {affiliate.email && <p className="text-gray-500">{affiliate.email}</p>}
        </div>
        <div className="flex gap-2">
          <button onClick={() => setEditingAffiliate(true)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Edit</button>
          <button onClick={() => { setEditingCode(null); setShowCodeModal(true); }} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">+ Market an app</button>
        </div>
      </div>

      <h2 className="text-lg font-semibold mb-3">Apps they market</h2>

      {markedAppIds.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-8 text-center">
          <p className="text-gray-500 mb-4">This affiliate isn&apos;t marketing any apps yet.</p>
          <button onClick={() => { setEditingCode(null); setShowCodeModal(true); }} className="text-blue-600 hover:text-blue-800">Add an app they market</button>
        </div>
      ) : (
        <div className="space-y-4">
          {markedAppIds.map((appId) => (
            <div key={appId} className="bg-white shadow rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-medium">{appsById[appId]?.name || `App #${appId}`}</h3>
              </div>
              <div className="divide-y divide-gray-100">
                {byApp[appId].map((code) => (
                  <div key={code.id} className="py-2 flex justify-between items-center">
                    <div>
                      <span className="font-mono text-sm">{code.code}</span>
                      {!code.active && <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">Inactive</span>}
                      <span className="ml-3 text-sm text-gray-500">Commission: {commissionLabel(code)}</span>
                    </div>
                    <div className="flex gap-3 items-center">
                      <button onClick={() => { setEditingCode(code); setShowCodeModal(true); }} className="text-blue-600 hover:text-blue-800 text-sm">Edit</button>
                      {code.active && <button onClick={() => handleDeactivateCode(code)} className="text-red-600 hover:text-red-800 text-sm">Deactivate</button>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {editingAffiliate && (
        <AffiliateModal affiliate={affiliate} onClose={() => setEditingAffiliate(false)} onSaved={() => { setEditingAffiliate(false); loadData(); }} />
      )}

      {showCodeModal && (
        <AffiliateCodeModal
          affiliateId={affiliate.id}
          apps={appList}
          code={editingCode}
          onClose={() => { setShowCodeModal(false); setEditingCode(null); }}
          onSaved={() => { setShowCodeModal(false); setEditingCode(null); loadData(); }}
        />
      )}
    </div>
  );
}

// Affiliate codes are always code_type=affiliate + reward_type=commission, so
// this modal only exposes the app, code string, commission, and destination.
function AffiliateCodeModal({ affiliateId, apps: appOptions, code, onClose, onSaved }) {
  const isEdit = !!code;
  const [appId, setAppId] = useState(code?.app_id || appOptions[0]?.id || '');
  const [codeStr, setCodeStr] = useState(code?.code || '');
  const [commissionPercent, setCommissionPercent] = useState(code?.commission_percent || 0);
  const [commissionDollars, setCommissionDollars] = useState(code ? (code.commission_cents || 0) / 100 : 0);
  const [destination, setDestination] = useState(code?.destination || '');
  const [active, setActive] = useState(code ? code.active : true);
  const [loadingCode, setLoadingCode] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // The embedded code (from the affiliate show) lacks destination, so fetch the
  // full record when editing to prefill every field.
  useEffect(() => {
    if (!isEdit) return;
    let active2 = true;
    referralCodes.get(code.app_id, code.id)
      .then((res) => {
        if (!active2) return;
        const full = res.data;
        setDestination(full.destination || '');
        setCommissionPercent(full.commission_percent || 0);
        setCommissionDollars((full.commission_cents || 0) / 100);
      })
      .catch(() => {})
      .finally(() => active2 && setLoadingCode(false));
    return () => { active2 = false; };
  }, [isEdit, code]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      const payload = {
        code: codeStr,
        code_type: 'affiliate',
        affiliate_id: affiliateId,
        reward_type: 'commission',
        commission_percent: Number(commissionPercent) || 0,
        commission_cents: Math.round((Number(commissionDollars) || 0) * 100),
        destination,
        active,
      };
      if (isEdit) {
        await referralCodes.update(appId, code.id, payload);
      } else {
        await referralCodes.create(appId, payload);
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.errors?.join(', ') || err.response?.data?.error || 'Failed to save code');
    } finally {
      setSaving(false);
    }
  };

  const hasCommission = Number(commissionPercent) > 0 || Number(commissionDollars) > 0;

  return (
    <Modal open onClose={onClose} title={isEdit ? 'Edit Affiliate Code' : 'Market an App'} size="md">
      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">{error}</div>}
      {loadingCode ? (
        <div className="text-gray-500 py-4">Loading code…</div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">App</label>
            <select value={appId} onChange={(e) => setAppId(e.target.value)} disabled={isEdit} className="w-full border border-gray-300 rounded-lg px-3 py-2 disabled:bg-gray-100" required>
              {appOptions.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
            <input type="text" value={codeStr} onChange={(e) => setCodeStr(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))} className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="e.g., jane20" required />
            <p className="mt-1 text-xs text-gray-500">The vanity slug, e.g. churchcare.net/<span className="font-mono">{codeStr || 'jane20'}</span></p>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Commission %</label>
              <input type="number" min="0" max="100" value={commissionPercent} onChange={(e) => setCommissionPercent(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Flat commission ($)</label>
              <input type="number" min="0" step="0.01" value={commissionDollars} onChange={(e) => setCommissionDollars(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
            </div>
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Destination (optional)</label>
            <input type="text" value={destination} onChange={(e) => setDestination(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="/pricing or https://…" />
            <p className="mt-1 text-xs text-gray-500">Where the vanity link redirects. Defaults to the app home if blank.</p>
          </div>
          <div className="mb-6">
            <label className="flex items-center">
              <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
              <span className="ml-2 text-sm text-gray-700">Active</span>
            </label>
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
            <button type="submit" disabled={saving || !codeStr || !appId || !hasCommission} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving...' : (isEdit ? 'Save' : 'Create')}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
